import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// The footer easter egg. A little circular "Bertis" that spins slowly and,
// every so often, spontaneously reverses direction. Tapping it spins it faster;
// on the 5th tap it bursts into a swarm of smaller circular copies that bounce
// around the viewport and slowly multiply (up to a cap) — then keep bouncing.
const SRC = '/images/people/berti-peek.jpg';
const ELSA = '/images/egg/elsa-glowing-eyes.jpg';
const CAP = 56;             // passive self-multiplication ceiling
const HARDCAP = 120;        // absolute ceiling (clicking can push past CAP)
const BURST = 12;           // how many the 5th tap ejects
const REVEAL_AT = 10;       // taps that trigger the "JAWA UNCOVERED" reveal

// Random quips the easter egg says in its speech bubble — mostly movie lines.
// Add more here anytime; one is picked at random on hover / tap.
const QUOTES = [
  "He's got the bloody map!",
  "This is no mine, it's a tomb!",
  "Dad, where are the letters?",
  "I got a jar of dirt!",
  "Oh yeah, it's all coming together.",
  "What's taters, Precious?",
  "Roast chicken?",
  "Is that a plane?",
  "Let me guess, spring cleaning?",
  "I've always been taller than you!",
];

interface Egg { id: number; x: number; y: number; vx: number; vy: number; r: number; }

// The final reveal: a corner portrait of Elsa whose glowing eyes pulse in a
// mostly-regular rhythm with a little randomness thrown in — colour and
// intensity both wander. Runs on a light interval; cleans itself up.
function ElsaEgg() {
  const eyesRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    let t = 0;
    let hue = 150;                       // start eerie green
    const id = window.setInterval(() => {
      t += 1;
      // Regular pulse (sine) plus a jitter, so it breathes but never mechanically.
      const base = 0.6 + 0.4 * Math.sin(t * 0.4);
      const glow = Math.max(0.15, Math.min(1, base + (Math.random() - 0.5) * 0.28));
      // Hue drifts slowly; occasionally jumps for an unsettling flick.
      hue = (hue + 3 + (Math.random() < 0.12 ? Math.random() * 90 : 0)) % 360;
      const el = eyesRef.current;
      if (el) {
        el.style.setProperty('--eye', `hsl(${Math.round(hue)} 100% ${Math.round(52 + glow * 20)}%)`);
        el.style.setProperty('--eye-glow', String(glow));
      }
    }, 85);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="elsa-egg" aria-hidden="true" title="JAWA">
      <img src={ELSA} alt="" className="elsa-egg__img" />
      <div className="elsa-egg__eyes" ref={eyesRef} />
    </div>
  );
}

export function PeekEgg() {
  const [taps, setTaps] = useState(0);
  const [dir, setDir] = useState<'normal' | 'reverse'>('normal');
  const [eggs, setEggs] = useState<Egg[]>([]);
  const [quote, setQuote] = useState<string | null>(null);
  const [phase, setPhase] = useState<'idle' | 'jawa' | 'elsa'>('idle');
  const jawaTimer = useRef<number | undefined>(undefined);
  const imgRef = useRef<HTMLImageElement>(null);
  const idRef = useRef(0);
  const eggsRef = useRef<Egg[]>([]);
  const rafRef = useRef<number | undefined>(undefined);
  const bubbleTimer = useRef<number | undefined>(undefined);
  eggsRef.current = eggs;

  // Pop the speech bubble with a fresh random quip (different from the last).
  function saySomething() {
    setQuote(prev => {
      let q = QUOTES[Math.floor(Math.random() * QUOTES.length)];
      if (QUOTES.length > 1) while (q === prev) q = QUOTES[Math.floor(Math.random() * QUOTES.length)];
      return q;
    });
    if (bubbleTimer.current) clearTimeout(bubbleTimer.current);
    bubbleTimer.current = window.setTimeout(() => setQuote(null), 3400);
  }
  useEffect(() => () => { if (bubbleTimer.current) clearTimeout(bubbleTimer.current); }, []);

  // Click a flying copy → it spawns a few more from that spot (up to the hard cap).
  function splitEgg(id: number) {
    const cur = eggsRef.current;
    if (cur.length >= HARDCAP) return;
    const parent = cur.find(e => e.id === id);
    if (!parent) return;
    const kids: Egg[] = [];
    for (let i = 0; i < 3; i++) {
      const a = Math.random() * Math.PI * 2;
      const sp = 2 + Math.random() * 3.5;
      kids.push({ id: idRef.current++, x: parent.x, y: parent.y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 3, r: Math.max(13, parent.r * 0.86) });
    }
    const next = cur.concat(kids).slice(0, HARDCAP);
    eggsRef.current = next;   // sync immediately so the rAF loop sees the newcomers
    setEggs(next);
  }

  // Spontaneous, occasional reversal of spin direction.
  useEffect(() => {
    let t: number;
    const tick = () => {
      t = window.setTimeout(() => {
        if (Math.random() < 0.6) setDir(d => (d === 'normal' ? 'reverse' : 'normal'));
        tick();
      }, 3000 + Math.random() * 4000);
    };
    tick();
    return () => clearTimeout(t);
  }, []);

  // Faster spin the more it's tapped (base 9s → down to ~0.4s).
  const dur = Math.max(0.4, 9 / (1 + taps * 1.6));

  useEffect(() => () => { if (jawaTimer.current) clearTimeout(jawaTimer.current); }, []);

  function onTap() {
    const t = taps + 1;
    setTaps(t);

    // The 10th tap: the swarm vanishes, "JAWA UNCOVERED" seizes the screen,
    // then Elsa takes up residence in the corner. (Only fires once.)
    if (t === REVEAL_AT && phase === 'idle') {
      eggsRef.current = [];
      setEggs([]);
      setQuote(null);
      setPhase('jawa');
      jawaTimer.current = window.setTimeout(() => setPhase('elsa'), 2800);
      return;
    }

    saySomething();
    if (t >= 5 && phase === 'idle' && eggsRef.current.length === 0) {
      const el = imgRef.current;
      const rect = el?.getBoundingClientRect();
      const cx = rect ? rect.left + rect.width / 2 : 48;
      const cy = rect ? rect.top + rect.height / 2 : window.innerHeight - 48;
      const seed: Egg[] = [];
      for (let i = 0; i < BURST; i++) {
        const a = Math.random() * Math.PI * 2;
        const sp = 2.5 + Math.random() * 4;
        seed.push({ id: idRef.current++, x: cx, y: cy, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 3.5, r: 26 + Math.random() * 14 });
      }
      setEggs(seed);
    }
  }

  // Physics + multiplication loop — runs only while the swarm is alive.
  const alive = eggs.length > 0;
  useEffect(() => {
    if (!alive) return;
    let last = performance.now();
    const step = (now: number) => {
      const dt = Math.min(2.5, (now - last) / 16.67);
      last = now;
      const W = window.innerWidth, H = window.innerHeight;
      let arr = eggsRef.current.map(e => {
        let { x, y, vx, vy } = e;
        const half = e.r / 2;
        vy += 0.07 * dt;                              // gentle gravity
        x += vx * dt; y += vy * dt;
        if (x < half) { x = half; vx = Math.abs(vx) * 0.88; }
        else if (x > W - half) { x = W - half; vx = -Math.abs(vx) * 0.88; }
        if (y < half) { y = half; vy = Math.abs(vy) * 0.88; }
        else if (y > H - half) { y = H - half; vy = -Math.abs(vy) * 0.82; vx *= 0.98; }
        return { ...e, x, y, vx, vy };
      });
      // Occasionally a copy spawns a smaller copy, until the cap.
      if (arr.length < CAP && Math.random() < 0.05 * dt) {
        const p = arr[Math.floor(Math.random() * arr.length)];
        if (p) {
          const a = Math.random() * Math.PI * 2;
          arr = arr.concat({ id: idRef.current++, x: p.x, y: p.y, vx: Math.cos(a) * 3, vy: Math.sin(a) * 3 - 2, r: Math.max(15, p.r * 0.9) });
        }
      }
      eggsRef.current = arr;
      setEggs(arr);
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [alive]);

  return (
    <>
      <img
        ref={imgRef}
        className="footer__peek"
        src={SRC}
        alt=""
        aria-hidden="true"
        title="👋"
        loading="lazy"
        onClick={onTap}
        onMouseEnter={saySomething}
        style={{ ['--peek-dur' as string]: `${dur}s`, animationDirection: dir } as React.CSSProperties}
      />
      {quote && <div className="peek-bubble" role="status">{quote}</div>}

      {phase === 'jawa' && createPortal(
        <div className="jawa-overlay" aria-hidden="true">
          <div className="jawa-text">JAWA UNCOVERED</div>
        </div>,
        document.body,
      )}
      {phase === 'elsa' && createPortal(<ElsaEgg />, document.body)}

      {alive && createPortal(
        <div className="peek-swarm" aria-hidden="true">
          {eggs.map(e => (
            <img
              key={e.id}
              src={SRC}
              alt=""
              className="peek-mini"
              onClick={() => splitEgg(e.id)}
              style={{ left: e.x, top: e.y, width: e.r, height: e.r }}
            />
          ))}
        </div>,
        document.body,
      )}
    </>
  );
}
